import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { money, STATUS_LABELS, PAYMENT_LABELS, dayLabel } from "@/lib/format";
import { Loader2, Phone, MessageSquare, ChevronDown, ChevronUp, Store, Truck, Building2, Printer } from "lucide-react";
import { toast } from "sonner";

const STATUS_ACTIONS = [
  { status: "preparing", label: "Start Preparing" },
  { status: "ready_for_pickup", label: "Ready" },
  { status: "out_for_delivery", label: "Out for Delivery" },
  { status: "delivered", label: "Delivered" },
  { status: "completed", label: "Complete" },
  { status: "cancelled", label: "Cancel Order" },
];

const METHOD_ICON = { pickup: Store, local_delivery: Truck, carmax_delivery: Building2 };
const FILTERS = [
  { k: "", label: "All" },
  { k: "awaiting_payment", label: "Awaiting Payment", type: "payment" },
  { k: "payment_submitted", label: "Payment Submitted", type: "payment" },
  { k: "preparing", label: "Preparing", type: "status" },
  { k: "local_delivery", label: "Delivery", type: "fulfillment" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [expanded, setExpanded] = useState(null);
  const [msgText, setMsgText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter.type) params[filter.type] = filter.k;
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (num, order_status) => {
    await api.patch(`/admin/orders/${num}/status`, { order_status });
    toast.success(`Status → ${STATUS_LABELS[order_status]}`);
    load();
  };
  const confirmPayment = async (num) => {
    await api.patch(`/admin/orders/${num}/payment`, { payment_status: "payment_confirmed" });
    toast.success("Payment confirmed");
    load();
  };
  const sendMsg = async (num) => {
    if (!msgText) return;
    await api.post(`/admin/orders/${num}/message`, { text: msgText });
    toast.success("Update sent to customer");
    setMsgText("");
    load();
  };

  const printLabel = (o) => {
    const d = o.fulfillment_details || {};
    const addr = o.fulfillment_method === "local_delivery" ? `${d.street || ""} ${d.apt || ""}, ${d.city || ""}, ${d.state || ""} ${d.zip || ""}` : "";
    const when = d.delivery_date ? `${d.delivery_date} ${d.delivery_time || ""}` : "";
    const items = o.items.map((it) => `<div>${it.quantity}&times; ${it.category === "cookie" ? "Cookie Pair (" + it.size_label + ")" : it.name + " &mdash; " + it.size_label}</div>`).join("");
    const w = window.open("", "_blank", "width=420,height=620");
    w.document.write(`<html><head><title>Label #${o.order_number}</title><style>
      body{font-family:Arial,sans-serif;padding:16px;color:#111}
      .box{border:2px solid #000;border-radius:8px;padding:16px}
      .brand{text-align:center;font-weight:bold;letter-spacing:2px;color:#4A0E17;border-bottom:2px solid #D4AF37;padding-bottom:8px;margin-bottom:10px}
      .num{font-size:26px;font-weight:bold}
      .row{margin:6px 0}.lbl{font-size:11px;color:#666;text-transform:uppercase}
      .items{margin-top:8px;border-top:1px dashed #999;padding-top:8px;font-size:14px;line-height:1.5}
    </style></head><body onload="window.print()">
      <div class="box">
        <div class="brand">OVERFLOW POPCORN CO.</div>
        <div class="row"><span class="lbl">Order</span><div class="num">#${o.order_number}</div></div>
        <div class="row"><span class="lbl">Customer</span><div>${o.customer.first_name} ${o.customer.last_name}</div></div>
        <div class="row"><span class="lbl">Phone</span><div>${o.customer.phone}</div></div>
        ${addr ? `<div class="row"><span class="lbl">Deliver To</span><div>${addr}</div></div>` : ""}
        ${when ? `<div class="row"><span class="lbl">Delivery Time</span><div>${when}</div></div>` : ""}
        <div class="items"><span class="lbl">Items</span>${items}</div>
        ${o.special_instructions ? `<div class="row"><span class="lbl">Notes</span><div>${o.special_instructions}</div></div>` : ""}
        <div class="row" style="margin-top:10px"><span class="lbl">Total</span><div class="num">$${o.total.toFixed(2)}</div></div>
      </div>
    </body></html>`);
    w.document.close();
  };

  return (
    <AdminLayout>
      <h1 className="font-serif text-3xl text-cream mb-6">Order Management</h1>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-4">
        {FILTERS.map((f) => (
          <button key={f.label} onClick={() => setFilter(f)} data-testid={`order-filter-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm border ${filter.label === f.label ? "border-gold bg-gold/15 text-gold" : "border-gold/20 text-cream/60"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={36} /></div>
      ) : orders.length === 0 ? (
        <p className="text-cream/50 py-10 text-center">No orders yet.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const Icon = METHOD_ICON[o.fulfillment_method];
            const d = o.fulfillment_details || {};
            const isOpen = expanded === o.order_number;
            return (
              <div key={o.order_number} className="gold-border-card rounded-xl bg-card-surface overflow-hidden" data-testid={`admin-order-${o.order_number}`}>
                <button onClick={() => setExpanded(isOpen ? null : o.order_number)} className="w-full flex items-center justify-between p-4 text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className="text-gold shrink-0" size={20} />
                    <div className="min-w-0">
                      <p className="font-mono text-gold font-semibold">#{o.order_number}</p>
                      <p className="text-cream text-sm truncate">{o.customer.first_name} {o.customer.last_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-mono text-cream">{money(o.total)}</p>
                      <p className="text-xs text-gold">{PAYMENT_LABELS[o.payment_status]?.split("—")[0]}</p>
                    </div>
                    {isOpen ? <ChevronUp className="text-cream/50" size={18} /> : <ChevronDown className="text-cream/50" size={18} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gold/20 p-4 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div><span className="text-cream/50">Status: </span><span className="text-cream">{STATUS_LABELS[o.order_status]}</span></div>
                      <div><span className="text-cream/50">Ordered: </span><span className="text-cream">{new Date(o.created_at).toLocaleString()}</span></div>
                      <div className="flex items-center gap-2">
                        <span className="text-cream/50">Phone:</span>
                        <a href={`tel:${o.customer.phone}`} className="text-gold flex items-center gap-1" data-testid={`call-customer-${o.order_number}`}><Phone size={14} />{o.customer.phone}</a>
                        <a href={`sms:${o.customer.phone}`} className="text-gold flex items-center gap-1 ml-2" data-testid={`text-customer-${o.order_number}`}><MessageSquare size={14} />Text</a>
                      </div>
                      {o.customer.email && <div><span className="text-cream/50">Email: </span><span className="text-cream">{o.customer.email}</span></div>}
                    </div>

                    {o.fulfillment_method === "local_delivery" && (
                      <p className="text-cream/70 text-sm">📍 {d.street}{d.apt ? `, ${d.apt}` : ""}, {d.city}, {d.state} {d.zip} {d.instructions && `— ${d.instructions}`}</p>
                    )}
                    {o.fulfillment_method === "carmax_delivery" && (
                      <p className="text-cream/70 text-sm">🏢 {d.department} · {d.delivery_day && dayLabel(d.delivery_day)} {d.delivery_time} {d.carmax_notes && `— ${d.carmax_notes}`}</p>
                    )}

                    <div className="bg-obsidian/50 rounded-lg p-3 space-y-1">
                      {o.items.map((it, i) => (
                        <div key={i} className="flex justify-between text-sm text-cream/80">
                          <span>{it.quantity}× {it.category === "cookie" ? `Cookie Pair (${it.size_label})` : `${it.name} — ${it.size_label}`}</span>
                          <span className="font-mono">{money(it.line_total)}</span>
                        </div>
                      ))}
                      {o.special_instructions && <p className="text-cream/50 text-xs pt-2 border-t border-gold/10">Note: {o.special_instructions}</p>}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => printLabel(o)} variant="outline" className="rounded-full text-xs px-4 py-2 h-auto bg-transparent border-gold/40 text-cream hover:bg-gold/10" data-testid={`print-label-${o.order_number}`}><Printer size={13} className="mr-1" /> Print Label</Button>
                      {o.payment_status !== "payment_confirmed" && (
                        <Button onClick={() => confirmPayment(o.order_number)} className="btn-gold rounded-full text-xs px-4 py-2 h-auto" data-testid={`confirm-payment-${o.order_number}`}>Confirm Payment</Button>
                      )}
                      {STATUS_ACTIONS.map((a) => (
                        <Button key={a.status} onClick={() => setStatus(o.order_number, a.status)} variant="outline"
                          className={`rounded-full text-xs px-4 py-2 h-auto bg-transparent border-gold/40 ${a.status === "cancelled" ? "text-destructive border-destructive/40 hover:bg-destructive/10" : "text-cream hover:bg-gold/10"}`}
                          data-testid={`status-${a.status}-${o.order_number}`}>
                          {a.label}
                        </Button>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Input className="bg-input border-gold/25 text-cream rounded-xl h-10 text-sm" placeholder="Send a status update to customer..." value={expanded === o.order_number ? msgText : ""} onChange={(e) => setMsgText(e.target.value)} data-testid={`message-input-${o.order_number}`} />
                      <Button onClick={() => sendMsg(o.order_number)} className="btn-gold rounded-xl h-10" data-testid={`send-message-${o.order_number}`}>Send</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
